import {
  DeleteTwoTone,
  PlusCircleTwoTone,
  SaveOutlined,
} from '@ant-design/icons';
import { Alert, Input, Modal } from 'antd';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Sort } from 'src/icons/Sort.icon';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import {
  getSRTypeList,
  getSRTypeMappings,
  updateSRMappings,
} from 'src/modules/SuperAdmin/store/actions/service_request';
import { getOrgSetting } from 'src/store/actions/settings.action';
import { getFromStorage } from '../../../../../../lib/storage';
import organizationEventEmitter from '../../../../../../utilities/organizationEmmiter';
import { SuperAdminTable } from '../../../baseComponents/Table';

const ServiceRequestTypeMappingPage = () => {
  const dispatch = useDispatch();

  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const [bulkSRUploadEnabled, setBulkSRUploadEnabled] = useState(
    currentPractitioner?.org_settings?.enable_service_request_upload ?? false
  );
  const { srTypeMappingsLoading, srTypeMappings, srTypeList } = useSelector(
    (state) => state?.serviceRequestState
  );

  const [selectedOrganizationUuid, setSelectedOrganizationUuid] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  const [_selectedOrganizationName, setSelectedOrganizationName] =
    useState(null);
  const [_organizationDropdown, setOrganizationDropdown] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [updatedValues, setUpdatedValues] = useState({});
  const [tableLoader, setTableLoader] = useState(false);
  const [_organizationName, setOrganizationName] = useState(
    currentPractitioner?.org_id
  );
  const [addedServiceRequestType, setAddedServiceRequestType] = useState(null);
  const [addServiceRequestTypeBoxVisible, setaddServiceRequestTypeBoxVisible] =
    useState(false);
  const [selectedServiceRequestType, setSelectedServiceRequestType] =
    useState(null);

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, []);

  useEffect(() => {
    setTableLoader(srTypeMappingsLoading);
  }, [srTypeMappingsLoading]);

  useEffect(() => {
    dispatch(getSRTypeMappings(selectedOrganizationUuid));
    dispatch(getSRTypeList(selectedOrganizationUuid));
  }, [selectedOrganizationUuid, dispatch]);

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let dataArr = [];
      getActiveOrganizationsList.forEach((key) => {
        dataArr.push({
          value: key?.org_id,
          label: key?.org_name,
          org_uuid: key?.org_uuid,
        });
      });
      setOrganizationDropdown(dataArr);
    }
  }, [getActiveOrganizationsList]);

  useEffect(() => {
    if (srTypeMappings) {
      let dataArr = [];
      srTypeList.forEach((type) => {
        dataArr.push({
          key: type['service_request_type_id'],
          mapping: srTypeMappings[type['service_request_type']] ?? [
            type['service_request_type'],
          ],
          recognizedServiceRequestTypeName: type['service_request_type'],
        });
      });
      setTableData(dataArr);

      dataArr = {};
      if (Array.isArray(srTypeList)) {
        srTypeList.forEach((type) => {
          dataArr[type['service_request_type']] = srTypeMappings[
            type['service_request_type']
          ] ?? [type['service_request_type']];
        });
      }
      setUpdatedValues(dataArr);
    }
  }, [selectedOrganizationUuid, srTypeMappings, srTypeList]);

  useEffect(() => {
    async function getOrgSettingData() {
      await dispatch(getOrgSetting(selectedOrganizationUuid)).then(
        (setting) => {
          setBulkSRUploadEnabled(
            setting?.enable_service_request_upload ?? false
          );
        }
      );
    }
    getOrgSettingData();
  }, [selectedOrganizationUuid, dispatch]);

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUuid(newOrg);
        handleSelectChange(newOrg);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleSelectChange = (option) => {
    const orgUuid = option?.org_uuid;
    setSelectedOrganizationUuid(orgUuid);
    setSelectedOrganizationName(option?.label);
    setOrganizationName(option?.value);
  };

  const columnNameSorter = (a, b) => {
    return a.recognizedServiceRequestTypeName.localeCompare(
      b.recognizedServiceRequestTypeName
    );
  };

  const handleDeleteServiceRequestType = (
    recognizedServiceRequestTypeName,
    serviceRequestTypeToDelete
  ) => {
    setUpdatedValues((prevValues) => ({
      ...prevValues,
      [recognizedServiceRequestTypeName]: prevValues[
        recognizedServiceRequestTypeName
      ].filter(
        (serviceRequestType) =>
          serviceRequestType !== serviceRequestTypeToDelete
      ),
    }));
  };

  const columns = [
    {
      title: (
        <span className="font-[700]">
          {' '}
          Service Request Type in Insight{' '}
          <Sort className={'sort-icon-service-request-type-mapping'} />
        </span>
      ),
      dataIndex: 'recognizedServiceRequestTypeName',
      key: 'recognizedServiceRequestTypeName',
      width: '50%',
      sorter: columnNameSorter,
    },
    {
      title: <span className="font-[700]"> Service Request Type in CSV</span>,
      width: '60%',
      render: (text, record) => {
        const serviceRequestTypeNames = updatedValues[
          record.recognizedServiceRequestTypeName
        ]?.map((serviceRequestType) => serviceRequestType?.trim());
        return (
          <div className="flex flex-wrap gap-2">
            {serviceRequestTypeNames?.map((serviceRequestType, index) => {
              return (
                <div
                  key={index}
                  className="relative group inline-flex items-center px-3 py-1 mt-2 rounded-3xl"
                  style={{ border: `1px solid #00D090` }}
                >
                  <span className="flex-grow text-center group-hover:pr-4 transition-all">
                    {serviceRequestType}
                  </span>
                  <DeleteTwoTone
                    className="absolute right-1 hidden group-hover:inline-block cursor-pointer rounded-full p-1 transition-all"
                    onClick={() =>
                      handleDeleteServiceRequestType(
                        record.recognizedServiceRequestTypeName,
                        serviceRequestType
                      )
                    }
                    twoToneColor="#eb2f96"
                  />
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      width: '25%',
      render: (text, record) => {
        return (
          <PlusCircleTwoTone
            twoToneColor="#00D090"
            style={{ fontSize: '19px' }}
            onClick={() => {
              setSelectedServiceRequestType(
                record.recognizedServiceRequestTypeName
              );
              setaddServiceRequestTypeBoxVisible(true);
            }}
          />
        );
      },
    },
  ];

  const handleAddServiceRequestTypeClose = () => {
    setaddServiceRequestTypeBoxVisible(false);
    setSelectedServiceRequestType(null);
    setAddedServiceRequestType(null);
  };

  const handleAddServiceRequestTypeSubmit = () => {
    updatedValues[selectedServiceRequestType] = [
      ...updatedValues[selectedServiceRequestType],
      addedServiceRequestType.trim(),
    ];

    setaddServiceRequestTypeBoxVisible(false);
    setSelectedServiceRequestType(null);
    setAddedServiceRequestType(null);
  };

  const handleFormSubmit = async (_values) => {
    let mapping = {};
    for (const [key, value] of Object.entries(updatedValues)) {
      for (const serviceRequestType of value) {
        if (serviceRequestType !== key) {
          mapping[serviceRequestType] = key;
        }
      }
    }
    let payload = {
      settings: {
        service_request_type_mapping: mapping,
      },
    };
    dispatch(
      updateSRMappings(
        payload,
        selectedOrganizationUuid,
        'service_request_type_mapping'
      )
    );
  };

  return (
    <>
      <div>
        <Formik initialValues={{}} onSubmit={handleFormSubmit}>
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <div className="flex flex-row sm:justify-between gap-4 lg:justify-between items-center">
                <div className="flex flex-col justify-center"></div>
                {bulkSRUploadEnabled && (
                  <div className="flex justify-end">
                    <div className="flex justify-end items-end">
                      <button
                        type="submit"
                        className="bg-[#00D090] hover:bg-[#059669] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
                      >
                        <SaveOutlined
                          className={`mr-2`}
                          width="14"
                          height="14"
                          viewBox="0 0 16 16"
                        />{' '}
                        Save all
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {bulkSRUploadEnabled ? (
                <div className="super-admin-table">
                  <div className="mt-5 ant-table rounded-xl shadow-xl">
                    <SuperAdminTable
                      className={`appointment-page-table`}
                      columns={columns}
                      data={tableData}
                      loading={tableLoader}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex justify-center items-center p-4 min-h-[60vh]">
                  <Alert
                    message="Bulk Service Request uploads are currently disabled for this organization."
                    type="info"
                    showIcon
                  />
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
      <Modal
        open={addServiceRequestTypeBoxVisible}
        closable={false}
        centered={true}
        styles={{ body: { height: '80px' } }}
        footer={[
          <div key="footer" className="flex justify-end gap-1">
            <button
              key="back"
              className="bg-[#A9A9A9] hover:bg-[#899499] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={handleAddServiceRequestTypeClose}
            >
              Cancel
            </button>
            <button
              key="submit"
              className="bg-[#00D090] hover:bg-[#059669] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={handleAddServiceRequestTypeSubmit}
              disabled={
                !addedServiceRequestType ||
                addedServiceRequestType.trim() === ''
              }
            >
              Add service request type
            </button>
          </div>,
        ]}
      >
        <div className="mb-4 flex flex-col gap-2">
          <p className="text-md">{selectedServiceRequestType}</p>
        </div>
        <div>
          <Input
            placeholder="Enter service request type name"
            onChange={(e) => setAddedServiceRequestType(e.target.value)}
            value={addedServiceRequestType}
          />
        </div>
      </Modal>
    </>
  );
};

export default ServiceRequestTypeMappingPage;
